import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./AccountPage.css";
import Loader from "../layouts/components/Loader";

const API_BASE = "https://api.mariamlyluxe.com/public";

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

/** Always uses latest token */
async function apiFetch(path, { method = "GET", body } = {}) {
  const headers = { Accept: "application/json" };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const opts = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, opts);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error(data?.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.errors = data?.errors;
    throw err;
  }
  return data;
}

function formatDate(val) {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return String(val);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export default function AccountPage() {
  const navigate = useNavigate();
  const authed = !!getToken();

  const [user, setUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState("");

  // ===== Change Password (Modal) =====
  const [showCP, setShowCP] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState(""); // ✅ fixed
  const [newPassword2, setNewPassword2] = useState("");
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState("");
  const [cpFieldErrors, setCpFieldErrors] = useState({});
  const [cpSuccess, setCpSuccess] = useState("");
  const firstInputRef = useRef(null);

  // ===== Address Management =====
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState("");
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesError, setZonesError] = useState("");

  // Add/Edit Address Modal
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    zone_id: "",
    full_address: "",
    more_details: "",
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [addressFieldErrors, setAddressFieldErrors] = useState({});
  const [addressSuccess, setAddressSuccess] = useState("");

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // ===== Orders Management =====
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("pending");
  const [ordersPagination, setOrdersPagination] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // ===== Logout =====
  const [logoutLoading, setLogoutLoading] = useState(false);

  // ===== Sidebar Navigation =====
  const [activeSection, setActiveSection] = useState("profile");

  const openCP = () => {
    setCpError("");
    setCpSuccess("");
    setCpFieldErrors({});
    setOldPassword("");
    setNewPassword("");
    setNewPassword2("");
    setShowCP(true);
  };
  const closeCP = () => setShowCP(false);

  useEffect(() => {
    if (showCP && firstInputRef.current) firstInputRef.current.focus();
  }, [showCP]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && showCP) closeCP();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCP]);

  // ===== Profile =====
  const loadProfile = async () => {
    if (!authed) {
      setUser(null);
      setLoadingProfile(false);
      toast.error("You are not logged in.");
      return;
    }
    setLoadingProfile(true);
    setProfileError("");
    try {
      const data = await apiFetch("/api/profile");
      setUser(data?.user ?? data ?? null);
    } catch (e) {
      toast.error(e.message || "Failed to load profile.");
      setUser(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    loadProfile();
    if (authed) {
      loadAddresses();
      loadZones();
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  useEffect(() => {
    if (authed) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderStatus]);

  const onChangePassword = async (e) => {
    e?.preventDefault?.();

    setCpError("");
    setCpSuccess("");
    setCpFieldErrors({});

    if (!oldPassword || !newPassword || !newPassword2) {
      toast.error("Please fill all fields.");
      return;
    }
    if (newPassword !== newPassword2) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }

    setCpLoading(true);
    try {
      const payload = {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirmation: newPassword2,
      };
      const resp = await apiFetch("/api/change-password", {
        method: "POST",
        body: payload,
      });
      toast.success(resp?.message || "Password changed successfully.");
      setTimeout(() => closeCP(), 800);
    } catch (e) {
      if (e.errors && typeof e.errors === "object") setCpFieldErrors(e.errors);
      toast.error(e.message || "Failed to change password.");
    } finally {
      setCpLoading(false);
    }
  };

  // ===== Address Management Functions =====
  const loadAddresses = async () => {
    if (!authed) return;
    setAddressesLoading(true);
    setAddressesError("");
    try {
      const data = await apiFetch("/api/addresses");
      setAddresses(data?.addresses || data?.data || []);
    } catch (e) {
      toast.error(e.message || "Failed to load addresses.");
      setAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  };

  const loadZones = async () => {
    setZonesLoading(true);
    setZonesError("");
    try {
      const data = await apiFetch("/api/zones");
      const zonesArray = Array.isArray(data) ? data : data?.zones || data?.data || [];
      setZones(zonesArray);
    } catch (e) {
      toast.error(e.message || "Failed to load zones.");
      setZones([]);
    } finally {
      setZonesLoading(false);
    }
  };

  const openAddressModal = (address = null) => {
    setEditingAddress(address);
    setAddressForm(
      address
        ? {
            first_name: address.first_name || "",
            last_name: address.last_name || "",
            phone_number: address.phone_number || "",
            zone_id: address.zone_id || "",
            full_address: address.full_address || "",
            more_details: address.more_details || "",
          }
        : {
            first_name: "",
            last_name: "",
            phone_number: "",
            zone_id: "",
            full_address: "",
            more_details: "",
          }
    );
    setAddressError("");
    setAddressSuccess("");
    setAddressFieldErrors({});
    setShowAddressModal(true);
  };

  const closeAddressModal = () => {
    setShowAddressModal(false);
    setEditingAddress(null);
    setAddressForm({
      first_name: "",
      last_name: "",
      phone_number: "",
      zone_id: "",
      full_address: "",
      more_details: "",
    });
    setAddressError("");
    setAddressSuccess("");
    setAddressFieldErrors({});
  };

  const onAddressFormChange = (field, value) => {
    setAddressForm((prev) => ({ ...prev, [field]: value }));
    if (addressFieldErrors[field]) {
      setAddressFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const onSubmitAddress = async (e) => {
    e?.preventDefault?.();

    setAddressError("");
    setAddressSuccess("");
    setAddressFieldErrors({});

    if (
      !addressForm.first_name ||
      !addressForm.last_name ||
      !addressForm.phone_number ||
      !addressForm.zone_id ||
      !addressForm.full_address
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    setAddressLoading(true);
    try {
      const payload = {
        first_name: addressForm.first_name,
        last_name: addressForm.last_name,
        phone_number: addressForm.phone_number,
        zone_id: parseInt(addressForm.zone_id),
        full_address: addressForm.full_address,
        more_details: addressForm.more_details,
      };

      if (editingAddress) {
        await apiFetch(`/api/addresses/${editingAddress.id}`, {
          method: "PUT",
          body: payload,
        });
        toast.success("Address updated successfully.");
      } else {
        await apiFetch("/api/addresses", {
          method: "POST",
          body: payload,
        });
        toast.success("Address added successfully.");
      }

      await loadAddresses();
      setTimeout(() => closeAddressModal(), 1000);
    } catch (e) {
      if (e.errors && typeof e.errors === "object") {
        setAddressFieldErrors(e.errors);
      }
      toast.error(e.message || "Failed to save address.");
    } finally {
      setAddressLoading(false);
    }
  };

  const openDeleteModal = (address) => {
    setAddressToDelete(address);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAddressToDelete(null);
    setDeleteLoading(false);
    setDeleteError("");
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;

    setDeleteLoading(true);
    setDeleteError("");
    try {
      await apiFetch(`/api/addresses/${addressToDelete.id}`, {
        method: "DELETE",
      });
      toast.success("Address deleted successfully.");
      await loadAddresses();
      closeDeleteModal();
    } catch (e) {
      toast.error(e.message || "Failed to delete address.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ===== Orders Management Functions =====
  const toggleOrderDetails = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const loadOrders = async () => {
    if (!authed) return;
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const data = await apiFetch(`/api/orders/my?status=${selectedOrderStatus}`);
      // Handle the actual API response structure
      if (data?.success && data?.orders) {
        setOrders(data.orders.data || []);
        setOrdersPagination({
          current_page: data.orders.current_page,
          last_page: data.orders.last_page,
          total: data.orders.total,
          per_page: data.orders.per_page,
          next_page_url: data.orders.next_page_url,
          prev_page_url: data.orders.prev_page_url
        });
      } else {
        setOrders(data?.orders || data?.data || []);
        setOrdersPagination(null);
      }
    } catch (e) {
      toast.error(e.message || "Failed to load orders.");
      setOrders([]);
      setOrdersPagination(null);
    } finally {
      setOrdersLoading(false);
    }
  };

  // ===== Logout Function =====
  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await apiFetch("/api/logout", {
        method: "POST",
      });
      // Clear tokens from localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      // Navigate to home page
      navigate("/");
    } catch (e) {
      // Even if logout fails on server, clear local tokens
      localStorage.removeItem("token");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      // Navigate to home page
      navigate("/");
    } finally {
      setLogoutLoading(false);
    }
  };

  // Sidebar navigation items
  const sidebarItems = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "addresses", label: "Addresses", icon: "📍" },
    { id: "orders", label: "Orders", icon: "📦" },
  ];

  if (!authed) {
    return (
      <section className="ap ap-wrap">
        <h1 className="ap-title">My Account</h1>
        <p className="ap-muted">You're not logged in.</p>
      </section>
    );
  }

  const knownFields = user
    ? [
        ["First name", user.fname],
        ["Last name", user.lname],
        ["Full name", [user.fname, user.lname].filter(Boolean).join(" ") || "-"],
        ["Username", user.username],
        ["Email", user.email],
        ["Gender", user.gender],
        ["Birthdate", formatDate(user.birthdate)],
      ]
    : [];

  const dynamicExtras =
    user &&
    Object.entries(user).filter(
      ([k]) =>
        ![
          "id",
          "role_id",
          "created_at",
          "updated_at",
          "fname",
          "lname",
          "username",
          "email",
          "gender",
          "birthdate",
        ].includes(k)
    );

  return (
    <div className="ap ap-layout">
      {/* Sidebar */}
      <aside className="ap-sidebar">
        <div className="ap-sidebar-header">
          <h2 className="ap-sidebar-title">My Account</h2>
        </div>
        <nav className="ap-sidebar-nav">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              className={`ap-sidebar-item ${activeSection === item.id ? 'ap-sidebar-item-active' : ''}`}
              onClick={() => setActiveSection(item.id)}
            >
              <span className="ap-sidebar-icon">{item.icon}</span>
              <span className="ap-sidebar-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="ap-main">
        <div className="ap-main-header">
          <h1 className="ap-main-title">
            {sidebarItems.find(item => item.id === activeSection)?.label}
          </h1>
        </div>

        <div className="ap-main-content">
          {/* PROFILE SECTION */}
          {activeSection === "profile" && (
            <div className="ap-card">
        <div className="ap-card-head">
          <h2 className="ap-card-title">Profile</h2>
          <div className="ap-card-actions">
            <button type="button" className="ap-btn ap-btn-outline" onClick={loadProfile}>
              Refresh
            </button>
            <button type="button" className="ap-btn ap-btn-primary" onClick={openCP}>
              Change password
            </button>
            <button 
              type="button" 
              className="ap-btn ap-btn-outline ap-btn-danger" 
              onClick={handleLogout}
              disabled={logoutLoading}
            >
              {logoutLoading ? <Loader /> : "Logout"}
            </button>
          </div>
        </div>

        {loadingProfile ? (
          <Loader />
        ) : user ? (
          <>
            <div className="ap-table-wrap">
              <table className="ap-kv-table">
                <tbody>
                  {knownFields.map(([label, value]) => (
                    <tr key={label}>
                      <th>{label}</th>
                      <td>{value ?? "-"}</td>
                    </tr>
                  ))}
                  {dynamicExtras.map(([k, v]) => (
                    <tr key={k}>
                      <th>{k}</th>
                      <td>{typeof v === "object" ? JSON.stringify(v) : String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="ap-muted">No profile data.</p>
        )}
            </div>
          )}

          {/* ADDRESSES SECTION */}
          {activeSection === "addresses" && (
            <div className="ap-card">
        <div className="ap-card-head">
          <h2 className="ap-card-title">Addresses</h2>
          <div className="ap-card-actions">
            <button type="button" className="ap-btn ap-btn-outline" onClick={loadAddresses}>
              Refresh
            </button>
            <button
              type="button"
              className="ap-btn ap-btn-primary"
              onClick={() => openAddressModal()}
            >
              Add Address
            </button>
          </div>
        </div>

        {addressesLoading ? (
          <Loader />
        ) : addresses.length > 0 ? (
          <div className="ap-addresses-list">
            {addresses.map((address) => (
              <div key={address.id} className="ap-address-item">
                <div className="ap-address-content">
                  <div className="ap-address-header">
                    <h3 className="ap-address-name">
                      {address.first_name} {address.last_name}
                    </h3>
                    <div className="ap-address-phone">{address.phone_number}</div>
                  </div>
                  <div className="ap-address-details">
                    <div className="ap-address-main">{address.full_address}</div>
                    {address.more_details && (
                      <div className="ap-address-extra">{address.more_details}</div>
                    )}
                    <div className="ap-address-zone">
                      Zone: {address.zone?.name || zones.find((z) => z.id === address.zone_id)?.name || `Zone ${address.zone_id}`}
                    </div>
                  </div>
                </div>
                <div className="ap-address-actions">
                  <button
                    type="button"
                    className="ap-btn ap-btn-outline ap-btn-sm"
                    onClick={() => openAddressModal(address)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="ap-btn ap-btn-outline ap-btn-sm ap-btn-danger"
                    onClick={() => openDeleteModal(address)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ap-empty-state">
            <p className="ap-muted">No addresses found. Add your first address to get started.</p>
          </div>
        )}
            </div>
          )}

          {/* ORDERS SECTION */}
          {activeSection === "orders" && (
            <div className="ap-card">
        <div className="ap-card-head">
          <h2 className="ap-card-title">My Orders</h2>
          <div className="ap-card-actions">
            <select
              className="ap-select"
              value={selectedOrderStatus}
              onChange={(e) => setSelectedOrderStatus(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button type="button" className="ap-btn ap-btn-outline" onClick={loadOrders}>
              Refresh
            </button>
          </div>
        </div>

        {ordersLoading ? (
          <Loader />
        ) : orders.length > 0 ? (
          <>
            <div className="ap-orders-list">
              {orders.map((order) => (
                <div key={order.order_id} className="ap-order-item">
                  <div className="ap-order-content">
                    <div className="ap-order-header">
                      <h3 className="ap-order-id">Order #{order.order_id}</h3>
                      <div className="ap-order-header-right">
                        <div className={`ap-order-status ap-order-status-${order.order_status}`}>
                          {order.order_status}
                        </div>
                        {order.order_products && order.order_products.length > 0 && (
                          <button
                            type="button"
                            className="ap-btn-eye"
                            onClick={() => toggleOrderDetails(order.order_id)}
                            aria-label={expandedOrders.has(order.order_id) ? "Hide order details" : "Show order details"}
                          >
                            {expandedOrders.has(order.order_id) ? "👁️" : "👁️‍🗨️"}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="ap-order-details">
                      <div className="ap-order-date">
                        Ordered: {formatDate(order.date_added)}
                      </div>
                      <div className="ap-order-total">
                        Total: ${order.total}
                      </div>
                      <div className="ap-order-subtotal">
                        Subtotal: ${order.subtotal} + Shipping: ${order.shipping}
                      </div>
                      {order.order_products && order.order_products.length > 0 && (
                        <div className="ap-order-items-summary">
                          Items: {order.order_products.length} item{order.order_products.length !== 1 ? 's' : ''}
                        </div>
                      )}
                      {order.address && (
                        <div className="ap-order-address">
                          Address: {order.address.first_name} {order.address.last_name}, {order.address.full_address}
                        </div>
                      )}
                      {order.payment_code && (
                        <div className="ap-order-payment">
                          Payment: {order.payment_code}
                        </div>
                      )}
                    </div>
                    
                    {/* Detailed Product Information - Only shown when expanded */}
                    {expandedOrders.has(order.order_id) && order.order_products && order.order_products.length > 0 && (
                      <div className="ap-order-details-expanded">
                        <div className="ap-order-items">
                          <div className="ap-order-items-header">
                            Order Items
                          </div>
                          <div className="ap-order-products">
                            {order.order_products.map((orderProduct) => (
                              <div key={orderProduct.order_product_id} className="ap-order-product">
                                <div className="ap-order-product-image">
                                  {orderProduct.product?.image_url ? (
                                    <img 
                                      src={orderProduct.product.image_url} 
                                      alt={orderProduct.product.name}
                                      className="ap-product-img"
                                    />
                                  ) : (
                                    <div className="ap-product-placeholder">
                                      📦
                                    </div>
                                  )}
                                </div>
                                <div className="ap-order-product-details">
                                  <div className="ap-order-product-name">
                                    {orderProduct.product?.name || 'Unknown Product'}
                                  </div>
                                  <div className="ap-order-product-info">
                                    <span className="ap-order-product-price">
                                      ${orderProduct.price}
                                    </span>
                                    <span className="ap-order-product-quantity">
                                      Qty: {orderProduct.quantity}
                                    </span>
                                    <span className="ap-order-product-total">
                                      Total: ${orderProduct.total}
                                    </span>
                                  </div>
                                  {orderProduct.product?.desc && (
                                    <div className="ap-order-product-desc">
                                      {orderProduct.product.desc}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {ordersPagination && ordersPagination.last_page > 1 && (
              <div className="ap-pagination">
                <div className="ap-pagination-info">
                  Showing {ordersPagination.current_page} of {ordersPagination.last_page} pages
                  ({ordersPagination.total} total orders)
                </div>
                <div className="ap-pagination-controls">
                  {ordersPagination.prev_page_url && (
                    <button 
                      className="ap-btn ap-btn-outline ap-btn-sm"
                      onClick={() => {
                        // Load previous page
                        console.log('Load previous page');
                      }}
                    >
                      Previous
                    </button>
                  )}
                  {ordersPagination.next_page_url && (
                    <button 
                      className="ap-btn ap-btn-outline ap-btn-sm"
                      onClick={() => {
                        // Load next page
                        console.log('Load next page');
                      }}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="ap-empty-state">
            <p className="ap-muted">No {selectedOrderStatus} orders found.</p>
          </div>
        )}
            </div>
          )}
        </div>
      </main>

      {/* ===== Modal: Change Password ===== */}
      {showCP && (
        <div
          className="ap-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-title"
          onMouseDown={(e) => {
            if (e.target.classList.contains("ap-modal-backdrop")) closeCP();
          }}
        >
          <div className="ap-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ap-modal-head">
              <h3 className="ap-modal-title" id="cp-title">
                Change Password
              </h3>
              <button className="ap-btn-icon" aria-label="Close" onClick={closeCP}>
                ✕
              </button>
            </div>

            <div className="ap-modal-body">
              <form onSubmit={onChangePassword} className="ap-form">
                <div className="ap-field">
                  <label className="ap-label">Current password</label>
                  <input
                    ref={firstInputRef}
                    className="ap-input"
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  {cpFieldErrors?.old_password && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(cpFieldErrors.old_password)
                        ? cpFieldErrors.old_password.join(", ")
                        : String(cpFieldErrors.old_password)}
                    </div>
                  )}
                </div>

                <div className="ap-field">
                  <label className="ap-label">New password</label>
                  <input
                    className="ap-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                  />
                  {cpFieldErrors?.new_password && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(cpFieldErrors.new_password)
                        ? cpFieldErrors.new_password.join(", ")
                        : String(cpFieldErrors.new_password)}
                    </div>
                  )}
                </div>

                <div className="ap-field">
                  <label className="ap-label">Confirm new password</label>
                  <input
                    className="ap-input"
                    type="password"
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                    placeholder="Repeat the new password"
                  />
                  {cpFieldErrors?.new_password_confirmation && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(cpFieldErrors.new_password_confirmation)
                        ? cpFieldErrors.new_password_confirmation.join(", ")
                        : String(cpFieldErrors.new_password_confirmation)}
                    </div>
                  )}
                </div>

                <div className="ap-modal-actions">
                  <button type="button" className="ap-btn ap-btn-outline" onClick={closeCP}>
                    Cancel
                  </button>
                  <button type="submit" className="ap-btn ap-btn-primary" disabled={cpLoading}>
                    {cpLoading ? <Loader /> : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal: Add/Edit Address ===== */}
      {showAddressModal && (
        <div
          className="ap-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="address-title"
          onMouseDown={(e) => {
            if (e.target.classList.contains("ap-modal-backdrop")) closeAddressModal();
          }}
        >
          <div className="ap-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ap-modal-head">
              <h3 className="ap-modal-title" id="address-title">
                {editingAddress ? "Edit Address" : "Add New Address"}
              </h3>
              <button className="ap-btn-icon" aria-label="Close" onClick={closeAddressModal}>
                ✕
              </button>
            </div>

            <div className="ap-modal-body">
              <form onSubmit={onSubmitAddress} className="ap-form">
                <div className="ap-form-row">
                  <div className="ap-field">
                    <label className="ap-label">First Name *</label>
                    <input
                      className="ap-input"
                      type="text"
                      value={addressForm.first_name}
                      onChange={(e) => onAddressFormChange("first_name", e.target.value)}
                      placeholder="Enter first name"
                    />
                    {addressFieldErrors?.first_name && (
                      <div className="ap-hint ap-hint-error">
                        {Array.isArray(addressFieldErrors.first_name)
                          ? addressFieldErrors.first_name.join(", ")
                          : String(addressFieldErrors.first_name)}
                      </div>
                    )}
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Last Name *</label>
                    <input
                      className="ap-input"
                      type="text"
                      value={addressForm.last_name}
                      onChange={(e) => onAddressFormChange("last_name", e.target.value)}
                      placeholder="Enter last name"
                    />
                    {addressFieldErrors?.last_name && (
                      <div className="ap-hint ap-hint-error">
                        {Array.isArray(addressFieldErrors.last_name)
                          ? addressFieldErrors.last_name.join(", ")
                          : String(addressFieldErrors.last_name)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ap-field">
                  <label className="ap-label">Phone Number *</label>
                  <input
                    className="ap-input"
                    type="tel"
                    value={addressForm.phone_number}
                    onChange={(e) => onAddressFormChange("phone_number", e.target.value)}
                    placeholder="Enter phone number"
                  />
                  {addressFieldErrors?.phone_number && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(addressFieldErrors.phone_number)
                        ? addressFieldErrors.phone_number.join(", ")
                        : String(addressFieldErrors.phone_number)}
                    </div>
                  )}
                </div>

                <div className="ap-field">
                  <label className="ap-label">Zone *</label>
                  <select
                    className="ap-input"
                    value={addressForm.zone_id}
                    onChange={(e) => onAddressFormChange("zone_id", e.target.value)}
                  >
                    <option value="">Select a zone</option>
                    {zones.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name || `Zone ${zone.id}`}
                      </option>
                    ))}
                  </select>
                  {addressFieldErrors?.zone_id && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(addressFieldErrors.zone_id)
                        ? addressFieldErrors.zone_id.join(", ")
                        : String(addressFieldErrors.zone_id)}
                    </div>
                  )}
                </div>

                <div className="ap-field">
                  <label className="ap-label">Full Address *</label>
                  <textarea
                    className="ap-input ap-textarea"
                    value={addressForm.full_address}
                    onChange={(e) => onAddressFormChange("full_address", e.target.value)}
                    placeholder="Enter complete address"
                    rows={3}
                  />
                  {addressFieldErrors?.full_address && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(addressFieldErrors.full_address)
                        ? addressFieldErrors.full_address.join(", ")
                        : String(addressFieldErrors.full_address)}
                    </div>
                  )}
                </div>

                <div className="ap-field">
                  <label className="ap-label">Additional Details</label>
                  <textarea
                    className="ap-input ap-textarea"
                    value={addressForm.more_details}
                    onChange={(e) => onAddressFormChange("more_details", e.target.value)}
                    placeholder="Landmarks, building info, etc. (optional)"
                    rows={2}
                  />
                  {addressFieldErrors?.more_details && (
                    <div className="ap-hint ap-hint-error">
                      {Array.isArray(addressFieldErrors.more_details)
                        ? addressFieldErrors.more_details.join(", ")
                        : String(addressFieldErrors.more_details)}
                    </div>
                  )}
                </div>

                <div className="ap-modal-actions">
                  <button
                    type="button"
                    className="ap-btn ap-btn-outline"
                    onClick={closeAddressModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="ap-btn ap-btn-primary" disabled={addressLoading}>
                    {addressLoading ? <Loader /> : editingAddress ? "Update Address" : "Add Address"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal: Delete Confirmation ===== */}
      {showDeleteModal && addressToDelete && (
        <div
          className="ap-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-title"
          onMouseDown={(e) => {
            if (e.target.classList.contains("ap-modal-backdrop")) closeDeleteModal();
          }}
        >
          <div className="ap-modal ap-delete-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ap-modal-head">
              <h3 className="ap-modal-title" id="delete-title">
                Delete Address
              </h3>
              <button className="ap-btn-icon" aria-label="Close" onClick={closeDeleteModal}>
                ✕
              </button>
            </div>

            <div className="ap-modal-body">
              <div className="ap-delete-content">
                <div className="ap-delete-icon">⚠️</div>
                <div className="ap-delete-text">
                  <p>Are you sure you want to delete this address?</p>
                  <div className="ap-delete-address-preview">
                    <strong>
                      {addressToDelete.first_name} {addressToDelete.last_name}
                    </strong>
                    <div>{addressToDelete.full_address}</div>
                    {addressToDelete.more_details && (
                      <div className="ap-delete-extra">{addressToDelete.more_details}</div>
                    )}
                  </div>
                  <p className="ap-delete-warning">This action cannot be undone.</p>
                </div>
              </div>

              <div className="ap-modal-actions">
                <button
                  type="button"
                  className="ap-btn ap-btn-outline"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ap-btn ap-btn-danger ap-btn-solid"
                  onClick={confirmDeleteAddress}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <Loader /> : "Delete Address"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}
